import type { EngineState } from './types';

export class CanvasRenderer {
  render(ctx: CanvasRenderingContext2D, state: EngineState): void {
    const { worldWidth, worldHeight } = state;

    // Clear Screen with deep dark arcade synth background
    ctx.fillStyle = '#05040a';
    ctx.fillRect(0, 0, worldWidth, worldHeight);

    // Draw Vector Synth Grid
    this.drawGrid(ctx, worldWidth, worldHeight);

    // Draw Core Matrix
    this.drawCore(ctx, state);

    // Draw Particles
    this.drawParticles(ctx, state);

    // Draw Shockwaves
    this.drawShockwaves(ctx, state);

    // Draw Bullets
    this.drawBullets(ctx, state);

    // Draw Untrapped Enemies
    this.drawEnemies(ctx, state);

    // Draw Trapped Bubbles
    this.drawBubbles(ctx, state);

    // Draw Player
    this.drawPlayer(ctx, state);

    // Draw Overlay / Start / Game Over state
    this.drawOverlay(ctx, state);
  }

  private drawGrid(ctx: CanvasRenderingContext2D, width: number, height: number): void {
    ctx.strokeStyle = 'rgba(0, 243, 255, 0.08)';
    ctx.lineWidth = 1;

    const gridSize = 40;
    const offset = (performance.now() * 0.02) % gridSize;

    ctx.beginPath();
    // Vertical lines
    for (let x = 0; x <= width; x += gridSize) {
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
    }
    // Horizontal lines scrolling downward
    for (let y = offset; y <= height; y += gridSize) {
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
    }
    ctx.stroke();
  }

  private drawCore(ctx: CanvasRenderingContext2D, state: EngineState): void {
    const { core } = state;
    const halfW = core.width / 2;
    const halfH = core.height / 2;

    ctx.save();
    ctx.translate(core.x, core.y);

    // Outer Matrix Barrier
    ctx.strokeStyle = '#00f3ff';
    ctx.shadowColor = '#00f3ff';
    ctx.shadowBlur = 12;
    ctx.lineWidth = 3;
    ctx.strokeRect(-halfW, -halfH, core.width, core.height);

    // Core Fill with health proportion
    const healthRatio = core.health / core.maxHealth;
    const fillW = (core.width - 8) * healthRatio;
    ctx.fillStyle = healthRatio > 0.3 ? '#00ffaa' : '#ff0055';
    ctx.fillRect(-halfW + 4, -halfH + 4, fillW, core.height - 8);

    // Text Label
    ctx.font = 'bold 11px system-ui, sans-serif';
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowBlur = 0;
    ctx.fillText(`CORE MATRIX ${Math.ceil(core.health)}%`, 0, 0);

    ctx.restore();
  }

  private drawParticles(ctx: CanvasRenderingContext2D, state: EngineState): void {
    ctx.save();
    for (const p of state.particles) {
      const alpha = p.life / p.maxLife;
      ctx.fillStyle = p.color;
      ctx.globalAlpha = Math.max(0, alpha);
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  private drawShockwaves(ctx: CanvasRenderingContext2D, state: EngineState): void {
    ctx.save();
    for (const sw of state.shockwaves) {
      const alpha = sw.life / sw.maxLife;
      ctx.strokeStyle = '#ff00ea';
      ctx.shadowColor = '#ff00ea';
      ctx.shadowBlur = 15;
      ctx.lineWidth = 3;
      ctx.globalAlpha = Math.max(0, alpha);

      ctx.beginPath();
      ctx.arc(sw.x, sw.y, sw.radius, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.restore();
  }

  private drawBullets(ctx: CanvasRenderingContext2D, state: EngineState): void {
    ctx.save();
    ctx.fillStyle = '#00ffff';
    ctx.shadowColor = '#00ffff';
    ctx.shadowBlur = 10;

    for (const b of state.bullets) {
      ctx.beginPath();
      ctx.arc(b.x, b.y, b.radius, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  private drawEnemies(ctx: CanvasRenderingContext2D, state: EngineState): void {
    ctx.save();
    for (const e of state.enemies) {
      if (e.trapped) continue;

      ctx.strokeStyle = e.color;
      ctx.fillStyle = e.color;
      ctx.shadowColor = e.color;
      ctx.shadowBlur = 10;
      ctx.lineWidth = 2;

      ctx.save();
      ctx.translate(e.x, e.y);

      if (e.type === 'runner') {
        // Fast triangle
        ctx.beginPath();
        ctx.moveTo(0, e.radius);
        ctx.lineTo(-e.radius * 0.8, -e.radius * 0.8);
        ctx.lineTo(e.radius * 0.8, -e.radius * 0.8);
        ctx.closePath();
        ctx.stroke();
      } else if (e.type === 'heavy') {
        // Hexagon
        ctx.beginPath();
        for (let i = 0; i < 6; i++) {
          const angle = (i * Math.PI) / 3;
          const px = Math.cos(angle) * e.radius;
          const py = Math.sin(angle) * e.radius;
          if (i === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.closePath();
        ctx.stroke();
      } else {
        // Pulsar Diamond
        ctx.beginPath();
        ctx.moveTo(0, -e.radius);
        ctx.lineTo(e.radius, 0);
        ctx.moveTo(0, e.radius);
        ctx.lineTo(-e.radius, 0);
        ctx.closePath();
        ctx.stroke();
      }

      ctx.restore();
    }
    ctx.restore();
  }

  private drawBubbles(ctx: CanvasRenderingContext2D, state: EngineState): void {
    ctx.save();
    for (const b of state.bubbles) {
      const alpha = b.popping ? 1 - b.popTimer : 0.85;
      const currentRadius = b.popping
        ? b.radius * (1 + b.popTimer * 0.5)
        : b.radius;

      ctx.save();
      ctx.translate(b.x, b.y);
      ctx.globalAlpha = Math.max(0, alpha);

      // Iridescent Glowing Bubble
      ctx.strokeStyle = '#00f3ff';
      ctx.shadowColor = '#00f3ff';
      ctx.shadowBlur = 12;
      ctx.lineWidth = 3;

      ctx.beginPath();
      ctx.arc(0, 0, currentRadius, 0, Math.PI * 2);
      ctx.stroke();

      // Bubble sheen highlight
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(
        -currentRadius * 0.3,
        -currentRadius * 0.3,
        currentRadius * 0.4,
        Math.PI,
        Math.PI * 1.5
      );
      ctx.stroke();

      // Trapped enemy inside
      if (b.trappedEnemy) {
        ctx.strokeStyle = b.trappedEnemy.color;
        ctx.beginPath();
        ctx.arc(0, 0, b.trappedEnemy.radius * 0.7, 0, Math.PI * 2);
        ctx.stroke();
      }

      ctx.restore();
    }
    ctx.restore();
  }

  private drawPlayer(ctx: CanvasRenderingContext2D, state: EngineState): void {
    const { player } = state;

    // Blink if invulnerable
    if (player.invulnTimer > 0 && Math.floor(player.invulnTimer * 10) % 2 === 0) {
      return;
    }

    ctx.save();
    ctx.translate(player.x, player.y);
    ctx.rotate(player.angle);

    ctx.strokeStyle = '#00ffaa';
    ctx.fillStyle = '#00ffaa';
    ctx.shadowColor = '#00ffaa';
    ctx.shadowBlur = 12;
    ctx.lineWidth = 2.5;

    // Vector Craft Triangle Shape
    ctx.beginPath();
    ctx.moveTo(player.radius * 1.2, 0);
    ctx.lineTo(-player.radius * 0.8, -player.radius * 0.9);
    ctx.lineTo(-player.radius * 0.4, 0);
    ctx.lineTo(-player.radius * 0.8, player.radius * 0.9);
    ctx.closePath();
    ctx.stroke();

    // Thruster flame trail
    ctx.strokeStyle = '#ff00ea';
    ctx.shadowColor = '#ff00ea';
    ctx.beginPath();
    ctx.moveTo(-player.radius * 0.5, 0);
    ctx.lineTo(-player.radius * 1.3, (Math.random() - 0.5) * 8);
    ctx.stroke();

    ctx.restore();
  }

  private drawOverlay(ctx: CanvasRenderingContext2D, state: EngineState): void {
    const { worldWidth, worldHeight, status, score, wave, combo } = state;

    if (status === 'start') {
      ctx.fillStyle = 'rgba(5, 4, 10, 0.85)';
      ctx.fillRect(0, 0, worldWidth, worldHeight);

      ctx.font = 'bold 32px system-ui, sans-serif';
      ctx.fillStyle = '#00f3ff';
      ctx.textAlign = 'center';
      ctx.shadowColor = '#00f3ff';
      ctx.shadowBlur = 15;
      ctx.fillText('CHRONO DEFENDER', worldWidth / 2, worldHeight / 2 - 60);

      ctx.font = 'bold 16px system-ui, sans-serif';
      ctx.fillStyle = '#ff00ea';
      ctx.shadowColor = '#ff00ea';
      ctx.fillText('RECOIL RIFT', worldWidth / 2, worldHeight / 2 - 25);

      ctx.font = '14px system-ui, sans-serif';
      ctx.fillStyle = '#ffffff';
      ctx.shadowBlur = 0;
      ctx.fillText(
        'TRAP ENEMIES IN CHRONO BUBBLES & POP THEM FOR COMBO CHAINS!',
        worldWidth / 2,
        worldHeight / 2 + 30
      );

      ctx.font = 'bold 16px system-ui, sans-serif';
      ctx.fillStyle = '#00ffaa';
      ctx.fillText('PRESS FIRE OR [R] TO START', worldWidth / 2, worldHeight / 2 + 80);
    } else if (status === 'gameover') {
      ctx.fillStyle = 'rgba(15, 0, 10, 0.85)';
      ctx.fillRect(0, 0, worldWidth, worldHeight);

      ctx.font = 'bold 36px system-ui, sans-serif';
      ctx.fillStyle = '#ff0055';
      ctx.textAlign = 'center';
      ctx.shadowColor = '#ff0055';
      ctx.shadowBlur = 20;
      ctx.fillText('GAME OVER', worldWidth / 2, worldHeight / 2 - 50);

      ctx.font = 'bold 20px system-ui, sans-serif';
      ctx.fillStyle = '#ffffff';
      ctx.shadowBlur = 0;
      ctx.fillText(`FINAL SCORE: ${score} · WAVE ${wave}`, worldWidth / 2, worldHeight / 2);

      ctx.font = 'bold 16px system-ui, sans-serif';
      ctx.fillStyle = '#00f3ff';
      ctx.fillText('PRESS FIRE OR [R] TO RESTART', worldWidth / 2, worldHeight / 2 + 50);
    }

    // In-game HUD Combo & Message
    if (status === 'playing') {
      if (combo > 1) {
        ctx.font = 'bold 18px system-ui, sans-serif';
        ctx.fillStyle = '#ffcc00';
        ctx.textAlign = 'left';
        ctx.shadowColor = '#ffcc00';
        ctx.shadowBlur = 10;
        ctx.fillText(`${combo}x COMBO CHAIN!`, 20, 40);
      }
    }
  }
}
